import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LockIcon, Mail } from "lucide-react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Server } from "@modules/api";
import { onLogin, handlingClose } from "@utils/helpers/nativeFunctions";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import {
  changePassword,
  useChangePassword,
  useSignInWithB2B,
} from "@lib/stores";
import FormTextInput from "@components/items/inputs/FormTextInput";
import { Card } from "@ui/card";
import { translate } from "@modules/localizations/translate";
import { AppDownload } from "@components/layouts/b2c/onboarding/AppDownload";

import { useB2BLogin } from "./useB2BLogin";

export const B2BLogin = () => {
  const navigate = useNavigate();

  const location = useLocation();

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    showPasswordForm,
    handleSubmitEmail,
    handleSubmit,
    control,
    controlEmail,
    onSubmit,
    onSubmitEmail,
    status,
    checkLoginOption,
    resendPasswordCode,
    isPasswordExpired,
    password,
    formStateEmail,
    formState,
    onCancel,
    onCreatePasswordFormCancel,
    emailSearchParam,
  } = useB2BLogin("ar");

  const isEmailFormDisabled =
    !formStateEmail.isDirty ||
    Object.keys(formStateEmail.errors).length !== 0 ||
    checkLoginOption.status === "loading";

  const isDisabled =
    !formState.isDirty ||
    Object.keys(formState.errors).length !== 0 ||
    status === "loading";

  //   useEffect(() => {
  //     commonDispatch(CommonActions.setDir(Dir.RTL));
  //     commonDispatch(CommonActions.setLang('ar'));
  //   }, [commonDispatch]);

  const userAgent = navigator.userAgent.toLowerCase();

  return (
    <Card className="flex h-auto w-[434px] flex-col items-center justify-center rounded-xl border-none bg-dark_background py-3 text-white">
      <div className="flex h-full w-full max-w-[375px] flex-col items-center justify-center space-y-7">
        <div className="mb-2 px-9 text-center text-[22px] font-medium">
          {!showPasswordForm && !isPasswordExpired ? (
            <TranslatedText text="onboarding.titles.enter your organization email" />
          ) : !isPasswordExpired ? (
            <TranslatedText text="onboarding.titles.enter password" />
          ) : (
            <TranslatedText text="onboarding.titles.enter new password" />
          )}
        </div>
        {!showPasswordForm && !isPasswordExpired && (
          <form className="flex w-full flex-col space-y-2 px-6" noValidate>
            <FormTextInput
              inputHtmlType="email"
              inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
              prefix={
                <Mail className="dark:fill-dark-primary h-6 w-6 fill-primary text-[#D9D9D9]" />
              }
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                placeHolder: translate("onboarding.inputs.placeholders.email"),
                required: true,
                fieldOptions: {
                  // pattern: emailRegex()
                },
              }}
              control={controlEmail}
              name="email"
              validationErrorMessages={{
                pattern: "invalid email",
              }}
              onEnter={() => {
                handleSubmitEmail(onSubmitEmail)();
              }}
              required
            />
            <br />
            <div className="flex w-full items-center justify-center gap-5">
              <ShadcnButton
                className="bg-transparent hover:bg-transparent/20 dark:bg-transparent dark:hover:bg-transparent/20"
                onClick={(e) => {
                  e.preventDefault();
                  console.log(
                    "location",
                    !!(location.state || (location.search && emailSearchParam)),
                    {
                      state: location.state,
                      search: location.search,
                      emailSearchParam,
                    }
                  );
                  navigate(-1);
                  handlingClose();
                }}
                type="button"
              >
                <TranslatedText
                  text={`onboarding.buttons.${"cancel"}`}
                  className="text-[16px] font-normal leading-[22px] text-white"
                />
              </ShadcnButton>
              <ShadcnButton
                disabled={isEmailFormDisabled}
                className="px-6 text-white"
                onClick={handleSubmitEmail(onSubmitEmail)}
                isLoading={checkLoginOption.status === "loading"}
              >
                <TranslatedText
                  text={`onboarding.buttons.${"continue"}`}
                  className="text-[16px] font-normal leading-[22px]"
                />
              </ShadcnButton>
            </div>
          </form>
        )}
        {showPasswordForm && !isPasswordExpired && (
          <form className="flex w-full flex-col space-y-2 px-6" noValidate>
            <div className="hidden">
              <FormTextInput
                inputHtmlType="email"
                prefix={<Mail className="h-5 w-5 text-primary" />}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  placeHolder: translate(
                    "onboarding.inputs.placeholders.email"
                  ),
                  required: true,
                  fieldOptions: {},
                }}
                control={control}
                defaultValue=""
                name="email"
                required
              />
            </div>
            <FormTextInput
              inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
              prefix={
                <LockIcon className="dark:text-dark-primary h-5 w-5 text-primary" />
              }
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                placeHolder: translate(
                  "onboarding.inputs.placeholders.password"
                ),
                required: true,
                fieldOptions: {
                  minLength: 8,
                },
              }}
              control={control}
              defaultValue=""
              inputHtmlType={showPassword ? "text" : "password"}
              name="password"
              suffix={
                formState.dirtyFields.password ? (
                  <div
                    className="mt-1 cursor-pointer ltr:mr-1 rtl:ml-1"
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                  >
                    {showPassword ? (
                      <EyeOff color="gray" size={25} />
                    ) : (
                      <Eye color="gray" size={25} />
                    )}
                  </div>
                ) : (
                  <></>
                )
              }
              onEnter={() => {
                handleSubmit(onSubmit)();
              }}
              required
            />
            <br />
            <div className="flex w-full items-center justify-center gap-5">
              <ShadcnButton
                className="bg-transparent hover:bg-transparent/20 dark:bg-transparent dark:hover:bg-transparent/20"
                onClick={onCancel}
                type="button"
              >
                <TranslatedText
                  text={`onboarding.buttons.${"cancel"}`}
                  className="text-[16px] font-normal leading-[22px] text-white"
                />
              </ShadcnButton>
              <ShadcnButton
                disabled={isDisabled}
                className="px-6 text-white"
                onClick={handleSubmit(onSubmit)}
                isLoading={status === "loading"}
              >
                <TranslatedText
                  text={`onboarding.buttons.${"sign in"}`}
                  className="text-[16px] font-normal leading-[22px]"
                />
              </ShadcnButton>
            </div>
            <div
              className="dark:text-primary-dark dark:text-dark-primary mt-3 flex cursor-pointer items-center justify-center text-center text-[15px] font-normal text-primary"
              onClick={async () => {
                await resendPasswordCode(false);
              }}
            >
              <TranslatedText text="onboarding.titles.forgot your password?" />
            </div>
          </form>
        )}
        {isPasswordExpired && password && (
          <CreatePasswordForm
            oldPassword={password || ""}
            onCancel={onCreatePasswordFormCancel}
          />
        )}
      </div>
      {userAgent.includes("faylasof") || userAgent.includes("wajeez") ? null : (
        <div className="w-full pb-4">
          <AppDownload />
        </div>
      )}
      {/* <div className="pt-8 pb-4 w-full">
        <div className="font-medium text-center flex justify-center items-center gap-1">
          <span className="text-white">
            <TranslatedText text="onboarding.titles.have you faced problems?" />
          </span>
          <span className="text-primary dark:text-primary-dark">
            <Link to="/ar/contact-us">
              <TranslatedText text="onboarding.titles.contact us" />
            </Link>
          </span>
        </div>
      </div> */}
    </Card>
  );
};

interface CreatePasswordFormProps {
  oldPassword: string;
  onCancel: () => void;
}

type Inputs = {
  oldPassword: string;
  newPassword: string;
};

const CreatePasswordForm = ({
  oldPassword,
  onCancel,
}: CreatePasswordFormProps) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const b2bLogin = useSignInWithB2B();
  const { status: changePasswordStatus } = useChangePassword();

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors, dirtyFields },
  } = useForm<Inputs>({ mode: "all", defaultValues: { oldPassword } });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!b2bLogin.data?.token) return;
    changePassword(data, b2bLogin.data?.token, () => {
      const languageId = 1;

      const data = {
        ...b2bLogin.data,
        languageId,
      };

      onLogin(JSON.stringify(data));
    });
  };

  const isDisabled =
    !isDirty ||
    Object.keys(errors).length !== 0 ||
    changePasswordStatus === "loading";

  return (
    <div className="w-full">
      <form
        className="flex w-full flex-col space-y-2 px-6"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <FormTextInput
          inputHtmlType={showPassword ? "text" : "password"}
          inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
          prefix={<LockIcon className="h-5 w-5 text-primary" />}
          suffix={
            dirtyFields.newPassword ? (
              <div
                className="mt-1 cursor-pointer ltr:mr-1 rtl:ml-1"
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
              >
                {showPassword ? (
                  <EyeOff color="gray" size={25} />
                ) : (
                  <Eye color="gray" size={25} />
                )}
              </div>
            ) : (
              <></>
            )
          }
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            placeHolder: translate(
              "onboarding.inputs.placeholders.new password"
            ),
            required: true,
            fieldOptions: {
              minLength: 8,
            },
          }}
          control={control}
          name="newPassword"
          validationErrorMessages={{
            minLength: "The password must be at least 8 characters",
          }}
          onEnter={() => {
            handleSubmit(onSubmit)();
          }}
          required
        />
        <br />
        <div className="flex w-full items-center justify-center gap-5">
          <ShadcnButton
            className="bg-transparent hover:bg-transparent/20"
            onClick={onCancel}
            type="button"
          >
            <TranslatedText
              text={`onboarding.buttons.${"cancel"}`}
              className="text-[16px] font-normal leading-[22px] text-white"
            />
          </ShadcnButton>
          <ShadcnButton
            disabled={isDisabled}
            className="px-6 text-white"
            onClick={handleSubmit(onSubmit)}
            isLoading={changePasswordStatus === "loading"}
          >
            <TranslatedText
              text={`onboarding.buttons.${"reset password"}`}
              className="text-[16px] font-normal leading-[22px]"
            />
          </ShadcnButton>
        </div>
      </form>
    </div>
  );
};
